import { motion as Motion } from "framer-motion";
import cls from "./DareStageCard.module.scss";

function isDisplayableUrl(value) {
  if (typeof value !== "string") return false;
  const normalized = value.trim().toLowerCase();
  if (!normalized) return false;

  return (
    normalized.startsWith("http://") ||
    normalized.startsWith("https://") ||
    normalized.startsWith("//") ||
    normalized.startsWith("data:") ||
    normalized.startsWith("blob:") ||
    normalized.startsWith("/")
  );
}

function resolveDisplayImage(question, slot = 1) {
  const galleryType = slot === 1 ? "display_1" : "display_2";
  const keyField = slot === 1 ? "displayImage1Key" : "displayImage2Key";
  const urlField = slot === 1 ? "displayImage1Url" : "displayImage2Url";

  const gallery = Array.isArray(question?.questionImageGallery)
    ? question.questionImageGallery
    : [];

  const galleryEntry = gallery.find((item) => {
    const type = String(item?.type || "").toLowerCase();
    return type === galleryType;
  });

  const candidates = [
    galleryEntry?.url,
    question?.[urlField],
    question?.[keyField],
    galleryEntry?.key,
  ];

  for (const candidate of candidates) {
    const value = typeof candidate === "string" ? candidate.trim() : "";
    if (!value) continue;
    if (isDisplayableUrl(value)) return value;
  }

  return "";
}

export default function DareStageCard({
  turn,
  penaltyView,
  loading,
  onConfirmDareDone,
}) {
  const role = penaltyView?.role || "viewer";
  const winner = penaltyView?.winner || null;
  const loser = penaltyView?.loser || null;

  const turnData = turn || penaltyView?.turn || {};
  const question = turnData?.questionSnapshot || {};

  const loserPlayerId = String(turnData?.loserPlayerId || "");
  const answerPlayerId = String(turnData?.answerPlayerId || "");
  const guessPlayerId = String(turnData?.guessPlayerId || "");

  const answeringLost = Boolean(
    loserPlayerId && loserPlayerId === answerPlayerId,
  );
  const guessingLost = Boolean(
    loserPlayerId && loserPlayerId === guessPlayerId,
  );

  const dareText =
    (answeringLost ? question?.dareForAnswering : "") ||
    (guessingLost ? question?.dareForGuessing : "") ||
    turnData?.activeDareText ||
    "No dare text configured.";

  const dareImage = answeringLost
    ? resolveDisplayImage(question, 1)
    : guessingLost
      ? resolveDisplayImage(question, 2)
      : "";

  const dareLabel = answeringLost
    ? `Dare for ${loser?.displayName || "-"} `
    : guessingLost
      ? `Dare for ${loser?.displayName || "-"} `
      : "Active Dare";

  return (
    <section className={cls.stageShell}>
      <div className={cls.glowPink} />
      <div className={cls.glowBlue} />
      <div className={cls.gridLines} />

      <div className={cls.stageHeader}>
        <p className={cls.kicker}>DARE STAGE</p>
        <h3 className={cls.title}>Dare in Progress 😈</h3>
        <p className={cls.subtitle}>
          <strong>{winner?.displayName || "Winner"}</strong> takes the win…
          <strong>{loser?.displayName || "Loser"}</strong>, your dare starts
          now.
        </p>
      </div>

      <div className={cls.playerStrip}>
        <article className={cls.playerMiniCard}>
          <p className={cls.infoLabel}>Winner</p>
          <p className={cls.playerName}>{winner?.displayName || "-"}</p>
        </article>

        <article className={cls.playerMiniCard}>
          <p className={cls.infoLabel}>Loser</p>
          <p className={cls.playerName}>{loser?.displayName || "-"}</p>
        </article>
      </div>

      <div className={cls.darePanel}>
        {dareImage ? (
          <div className={cls.backgroundWrap}>
            <div className={cls.backgroundMedia}>
              <Motion.img
                src={dareImage}
                alt={dareLabel}
                className={cls.dareImage}
                initial={{ scale: 1, y: 0 }}
                animate={{ scale: 1.05, y: -10 }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  repeatType: "reverse",
                  ease: "easeInOut",
                }}
              />
            </div>
            <div className={cls.backgroundTopFade} />
            <div className={cls.backgroundBottomFade} />
            <div className={cls.backgroundGlow} />
          </div>
        ) : (
          <div className={cls.backgroundFallback} />
        )}

        <div className={cls.hudFrame}>
          <span className={cls.hudCornerTopLeft} />
          <span className={cls.hudCornerTopRight} />
          <span className={cls.hudCornerBottomLeft} />
          <span className={cls.hudCornerBottomRight} />
        </div>

        <div className={cls.overlay}>
          <span className={cls.dareTag}>{dareLabel}</span>
          <h4 className={cls.dareText}>{dareText}</h4>

          {role === "winner" ? (
            <button
              type="button"
              className={cls.primaryButton}
              onClick={() => onConfirmDareDone?.()}
              disabled={loading}
            >
              {loading ? "Loading..." : "Dare completed"}
            </button>
          ) : (
            <div className={cls.waitingBox}>
              👉 Waiting for {winner?.displayName || "Winner"} to confirm 😏
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
